import * as React from 'react'
import { createContext, PropsWithChildren, useContext, useEffect, useState } from 'react'
import { DataPromise, Destination, getDataEmptyState, IGraphArrayPayload, IGraphArrayResponse } from './AxiosContext'
import FileContext, { FileContextType } from '../context/FileContext'
import useEntity from '../hooks/useEntity'
import { ConvertIdResult, Message } from '@microsoft/microsoft-graph-types'
import { extractReferenceNumbersFromMessage, FILE_REFERENCE_NUMBER_PROP } from '../helpers/referenceNumberHelper'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import { useTranslation } from 'react-i18next'
import { Identity } from './IdentityContext'

export type EmailAddressDto = {
    name: string
    address: string
}

export type MessageDto = {
    internetMessageId: string
    conversationId: string
    subject: string
    sender: EmailAddressDto
    recipients: EmailAddressDto[]
}

export const mapEmailUser = (details: Office.EmailAddressDetails | Office.EmailUser): EmailAddressDto => ({
    name: details.displayName,
    address: details.emailAddress,
})

export const mapOutlookItem = (item: Office.MessageRead): MessageDto => ({
    internetMessageId: item.internetMessageId,
    conversationId: item.conversationId,
    subject: item.subject,
    sender: mapEmailUser(item.sender),
    recipients: item.to.map(recipient => mapEmailUser(recipient)),
})

export type MessageContextType = {
    outlookMessage: Office.MessageRead | Office.MessageCompose
    itemId?: string
    setItemId: (itemId: string) => void
    immutableId: DataPromise<string>
    // If and only if fileIdMap is undefined, the mail has not been processed yet.
    fileIdMap?: Map<string, string>
    message: DataPromise<Message>
    resetMessage: () => void
}

const MessageContext = createContext<MessageContextType>({
    outlookMessage: undefined,
    itemId: undefined,
    setItemId: undefined,
    immutableId: getDataEmptyState(),
    fileIdMap: undefined,
    message: getDataEmptyState(),
    resetMessage: undefined,
})

function checkSharedMailboxEmail() {
    if (Office.context.mailbox.item?.getSharedPropertiesAsync) {
        Office.context.mailbox.item?.getSharedPropertiesAsync?.(result => {
            if (result.status === Office.AsyncResultStatus.Failed) {
                console.log(
                    'Error while detecting shared mailbox:',
                    result.error.code,
                    result.error.name,
                    result.error.message,
                )
                Identity.current.selectMailbox(Office.context.mailbox.userProfile.emailAddress)
                return
            }
            const sharedProperties = result.value
            console.log(`Shared mailbox detected: ${JSON.stringify(sharedProperties)}`)
            Identity.current.selectMailbox(result.value.targetMailbox)
        })
    } else {
        console.log("The message isn't part of a shared mailbox:", Office.context.mailbox.userProfile.emailAddress)
        Identity.current.selectMailbox(Office.context.mailbox.userProfile.emailAddress)
    }
}

export function MessageProvider(props: PropsWithChildren) {
    const [outlookMessage, setOutlookMessage] = useState<Office.MessageRead>()
    const [itemId, setItemId] = useState<string>()
    const { validateReferences }: FileContextType = useContext(FileContext)
    const { t: translate } = useTranslation()

    const [fileIdMap, setFileIdMap] = useState<Map<string, string>>()
    const [message] = useEntity<Message>(
        (itemId: string) => ({
            method: 'GET',
            url: `/messages/${itemId}`,
            baseURL: Destination.Email,
            params: { $expand: `singleValueExtendedProperties($filter=id eq '${FILE_REFERENCE_NUMBER_PROP}')` },
        }),
        itemId,
    )
    const [immutableId] = useEntity<string, IGraphArrayPayload<ConvertIdResult>>(
        (itemId: string) => ({
            method: 'POST',
            url: '/translateExchangeIds',
            baseURL: Destination.PrivateEmail,
            data: { inputIds: [itemId], sourceIdType: 'restId', targetIdType: 'restImmutableEntryId' },
        }),
        itemId,
        (result: IGraphArrayResponse<ConvertIdResult>) => result.data.value?.[0].targetId,
    )

    useEffect(() => {
        if (isOfficeInitialized()) {
            setOutlookMessage(Office.context.mailbox.item)

            if (Office.context.mailbox.addHandlerAsync) {
                Office.context.mailbox.addHandlerAsync(
                    Office.EventType.ItemChanged,
                    () => {
                        console.log('Detected switch to mailbox item', Office.context.mailbox.item?.subject)
                        setOutlookMessage(Office.context.mailbox.item)
                        checkSharedMailboxEmail()
                    },
                    result => {
                        if (result.status === Office.AsyncResultStatus.Failed) {
                            console.log(
                                'Error in switch detection to mailbox item:',
                                result.error.code,
                                result.error.name,
                                result.error.message,
                            )
                            throw new Error(`${result.error.code}  ${result.error.name}: ${result.error.message}`)
                        }
                    },
                )
            } else {
                console.log(
                    translate('file.pivot.document.error.outdated_outlook', {
                        host: Office.context.diagnostics.host,
                        version: Office.context.diagnostics.version,
                        platform: Office.context.diagnostics.platform,
                    }),
                )
                throw new Error(
                    translate('file.pivot.document.error.outdated_outlook', {
                        host: Office.context.diagnostics.host,
                        version: Office.context.diagnostics.version,
                        platform: Office.context.diagnostics.platform,
                    }),
                )
            }
        }
    }, [])

    useEffect(() => {
        if (outlookMessage?.itemId) {
            if (isOfficeInitialized()) {
                setItemId(
                    Office.context.mailbox
                        .convertToRestId(outlookMessage?.itemId, Office.MailboxEnums.RestVersion.v2_0)
                        .replaceAll('/', '-'),
                )
            }
        } else {
            setItemId(undefined)
        }
    }, [outlookMessage?.itemId])

    useEffect(() => {
        const references = extractReferenceNumbersFromMessage(message.data)

        if (references) {
            validateReferences(extractReferenceNumbersFromMessage(message.data)).then(setFileIdMap)
        } else {
            setFileIdMap(undefined)
        }
    }, [message.data])

    return (
        <MessageContext.Provider
            value={{
                outlookMessage,
                itemId,
                setItemId,
                immutableId,
                fileIdMap,
                message,
                resetMessage: () => setItemId(undefined),
            }}
        >
            {props.children}
        </MessageContext.Provider>
    )
}

export default MessageContext
